import React, { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/NeoToggle/neoToggle";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import { ChoiceButtonGrid } from "../../components/CustomComponents/Buttons/ChoiceButton/choiceButton";
import CategorySelector from "../../components/CustomComponents/modal/CategorySelector";
import ModifierGroupsSelector from "../../components/CustomComponents/modal/ModifierGroupsSelector";
import AllergenSelector from "../../components/CustomComponents/modal/AllergenSelector";
import { resolveSelectedAllergens, joinAllergenSolutions } from "../../components/CustomComponents/modal/allergenUtils.js";
import MenuGroupsSelector from "../../components/CustomComponents/modal/MenuGroupsSelector";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker";
import DebugMUIDatePicker from "../../components/CustomComponents/MUIDatePicker/DebugMUIDatePicker";
import "../../components/CustomComponents/MUIDatePicker/MUIDatePicker.css";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import "../../components/DateTimePicker/dateTimePicker.css";
import "../../components/DateTimePicker/materialUIOverrides.css";
import moment from "moment";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";
import useCreateFormDirtyBaseline from "../../hooks/useCreateFormDirtyBaseline.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ReportingCategoryModel from "../../components/CustomComponents/modal/reportingCategory";
import { Modifier } from "../modifiers/initializeModifier";
import SupplierSelector from "../../components/CustomComponents/modal/SupplierSelector.jsx";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";

function flattenCategoryTreeForSync(cats, out = []) {
  if (!Array.isArray(cats)) return out;
  for (const c of cats) {
    if (c) out.push(c);
    if (c?.subCategoriesList?.length)
      flattenCategoryTreeForSync(c.subCategoriesList, out);
  }
  return out;
}

const getCategoryId = (catOrId) => {
  if (catOrId == null || catOrId === "") return null;
  if (typeof catOrId === "object") {
    const id = catOrId.rootCat_ID ?? catOrId.rC_ID ?? catOrId.reportCat_ID;
    return id == null || id === "" ? null : String(id);
  }
  return String(catOrId);
};

const categoryIdsMatch = (a, b) => {
  const idA = getCategoryId(a);
  const idB = getCategoryId(b);
  return idA != null && idB != null && idA === idB;
};

/** Keep current reporting category when still selected; otherwise use first selected. */
const syncReportingCategoryWithSelection = (
  selectedCategories,
  currentReportingCategory
) => {
  const list = Array.isArray(selectedCategories)
    ? selectedCategories.filter(Boolean)
    : [];
  if (list.length === 0) return {};

  const currentId = getCategoryId(currentReportingCategory);
  if (currentId && currentId !== "0") {
    const stillSelected = list.find((cat) => categoryIdsMatch(cat, currentId));
    if (stillSelected) return stillSelected;
  }

  // No valid current selection — default to first selected category.
  return list[0] || {};
};

const CreateItem = () => {
  const { ProductType, IsCombo } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData, settingsData } = useSelector((state) => state.auth);
  const { hasInitiallyLoaded } = useProfileAndSettings();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const [formState, setFormState] = useState({
    menuItem_Name: "",
    menuItem_KitchenDisplayName: "",
    menuItem_Detail: "",
    menuItem_RootType: ProductType || "",
    menuItem_ColorCode: "",
    menuItem_SalePrice: "",
    menuItem_CostPrice: "",
    menuItem_TaxPercentage: "",
    menuItem_TakeAwayPrice: "",
    menuItem_DeliveryPrice: "",
    menuItem_ServingSize: "",
    menuItem_CaloriesPerServing: "",
    menuItem_PreparationTime: "",
    menuItem_SKU: "",
    menuItem_SEOTitle: "",
    menuItem_SEODescription: "",
    menuItem_Permalink: "",
    menuItem_ASTime: "",
    menuItem_AETime: "",
    menuItem_Rating: "",
    menuItem_BarCode: "",
    menuItem_Type: IsCombo ? "Combo" : "Product",
    menuItem_ImageAltText: "",
    retail_Markup: "0",
    delivery_Markup: "0",
    online_Markup: "0",
    current_Quantity: "0",
    reorder_Quantity: "0",
    reorder_Level: "0",
    availableQuantity: "0",
    orderQuantityLimit: "",
    menuItem_DefaultModifierTitle: "",
    brand_ID: "",
    ingredientID: "",
    parentMenuItemID: "",
    itsMasterProductID: "",
    reportCat_ID: "",
    stadardUnit: "",
    unitSize: "",
    mP_UnitOfSale: "",
    mP_VolumnOfSale: "",
    localizeID: "en",
    appMode: "",
  });

  const [errors, setErrors] = useState({
    menuItem_Name: "",
    menuItem_SalePrice: "",
  });

  const [toggles, setToggles] = useState({
    menuItem_Publish: true,
    is_ShowInKiosk: false,
    isSellOnTill: true,
    isActiveMobileApp: true,
    is_OutOfStock: false,
    skuEnabled: false,
    expiryDateEnabled: false,
    seoEnabled: false,
    modifierGroupsEnabled: false,
    suppliersEnabled: false,
    allergensEnabled: false,
    menuGroupsEnabled: false,
    productsEnabled: IsCombo || false,
    menuItem_Homefeatured: false,
    menuItem_featured: false,
    menuItem_IsModifier: false,
    menuItem_IsFavorite: false,
    menuItem_HasVariant: false,
    isArchieve: false,
    isAllowedtoSetPriceatThePointofSale: false,
    isTaxExempt: false,
    isPreventGainingLoyalty: false,
    isHaveCustomerSpecificPricing: false,
    isProductisScannableOnly: false,
    isMasterProduct: false,
    itHasAMasterProduct: false,
    menuItem_IsDefaultModifier: false,
    isShowNotificationExpDate: false,
    isShowSocialMediaLink: false,
  });

  const [imageState, setImageState] = useState({
    menuItem_Image: "",
    imageFile: null,
  });

  const [selectors, setSelectors] = useState({
    selectedParentCategory: [],
    selectedModifierGroups: [],
    selectedSuppliers: [],
    selectedAllergens: [],
    selectedMenuGroups: [],
    selectedProducts: [],
  });

  const [reportingCategory, setReportingCategory] = useState({});
  const [allAllergens, setAllAllergens] = useState([]);
  const [settings, setSettings] = useState({ s_VAT: "", isTaxEnabled: false });
  const [hasUserEditedTax, setHasUserEditedTax] = useState(false);
  const [modals, setModals] = useState({
    showParentCategoryModal: false,
    showReportingCategoryModal: false,
    showModifierModal: false,
    showSupplierModal: false,
    showAllergenModal: false,
    showMenuGroupModal: false,
    isModalOpen: false,
    showProductModal: false,
  });

  // Modal management to prevent multiple modals open simultaneously
  const openModal = (modalName) => {
    // Close all other modals first
    setModals(prev => {
      const newModals = {};
      Object.keys(prev).forEach(key => {
        newModals[key] = key === modalName;
      });
      return newModals;
    });
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
  };

  const closeAllModals = () => {
    setModals(prev => {
      const newModals = {};
      Object.keys(prev).forEach(key => {
        newModals[key] = false;
      });
      return newModals;
    });
    // Restore body scroll when modal is closed
    document.body.style.overflow = 'unset';
    document.body.classList.remove('modal-open');
  };

  // Clean up body styles on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, []);
  const [expiryDate, setExpiryDate] = useState(null);
  const [variantDetailModalOpen, setVariantDetailModalOpen] = useState(false);
  const [variantDetailIndex, setVariantDetailIndex] = useState(null);
  const [variantDetailDraft, setVariantDetailDraft] = useState(null);
  const [variantDetailExpiry, setVariantDetailExpiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const isDarkMode = useDarkMode();

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const markUserEdited = useCallback(() => {
    setHasUserEdited((prev) => prev || true);
  }, []);

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      if (!profileData) {
        showErrorToast("Profile data not available. Please refresh the page.");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData, hasInitiallyLoaded]);

  const fetchSettings = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      setLoading(true);
      // Use Redux state instead of API call
      if (!settingsData) {
        setSettings({ s_VAT: 0, isTaxEnabled: false });
        return;
      }

      const newSettings = {
        s_VAT: settingsData.s_VAT || 0,
        isTaxEnabled: settingsData.isTaxEnabled ?? false,
      };

      setSettings(newSettings);
    } catch (err) {
      showErrorToast("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [settingsData, hasInitiallyLoaded]);

  const fetchAllergens = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        "/api/Allergen/GetAllAllergensGlobal"
      );
      setAllAllergens(response.data.data || []);
    } catch (err) {
      console.error("Error fetching allergens:", err);
      setAllAllergens([]);
      showErrorToast("Failed to fetch allergens");
    }
  }, []);

  const dedupeModifierGroups = (groups) => {
    if (!Array.isArray(groups)) return [];
    const seen = new Set();
    return groups.filter((g) => {
      const id = g?.modifierGroupID ?? g?.ModifierGroupID;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  useEffect(() => {
    const handler = setTimeout(async () => {
      await Promise.all([
        fetchSettings(),
        fetchCompCurrency(),
        fetchAllergens(),
      ]);
    }, 500);
    return () => clearTimeout(handler);
  }, [fetchSettings, fetchCompCurrency, fetchAllergens]);

  

  useEffect(() => {
    if (
      !hasUserEditedTax &&
      !toggles.isTaxExempt &&
      settings.isTaxEnabled &&
      settings.s_VAT &&
      (!formState.menuItem_TaxPercentage || formState.menuItem_TaxPercentage === "")
    ) {
      setFormState((prev) => ({
        ...prev,
        menuItem_TaxPercentage: settings.s_VAT.toString(),
      }));
    } else if (toggles.isTaxExempt) {
      setFormState((prev) => ({
        ...prev,
        menuItem_TaxPercentage: "",
      }));
    }
  }, [
    toggles.isTaxExempt,
    settings.s_VAT,
    settings.isTaxEnabled,
    hasUserEditedTax,
    formState.menuItem_TaxPercentage,
  ]);

  // Keep reporting category in sync with selected categories without
  // overwriting a manual selection while it remains valid.
  useEffect(() => {
    setReportingCategory((prev) =>
      syncReportingCategoryWithSelection(
        selectors.selectedParentCategory,
        prev
      )
    );
  }, [selectors.selectedParentCategory]);

  // Update Modifier with reportCat_ID and menuItem_TaxPercentage
  useEffect(() => {
    Modifier.menuItem_TaxPercentage =
      !toggles.isTaxExempt && settings.isTaxEnabled && settings.s_VAT
        ? settings.s_VAT.toString()
        : "";
    Modifier.reportCat_ID =
      reportingCategory?.rootCat_ID || reportingCategory?.rC_ID
        ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID)
        : 0;
  }, [
    toggles.isTaxExempt,
    settings.isTaxEnabled,
    settings.s_VAT,
    reportingCategory,
  ]);

  const [variants, setVariants] = useState([
    {
      ...Modifier,
      menuItem_ServingSize: "",
      __taxEdited: false,
      menuItem_TaxPercentage:
        !toggles.isTaxExempt && settings.isTaxEnabled && settings.s_VAT
          ? settings.s_VAT.toString()
          : "",
      reportCat_ID:
        reportingCategory?.rootCat_ID || reportingCategory?.rC_ID
          ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID)
          : 0,
    },
  ]);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      formState,
      toggles,
      menuItem_Image: imageState.menuItem_Image || "",
      imageFileName: imageState.imageFile?.name || "",
      selectors: {
        selectedParentCategory: (selectors.selectedParentCategory || []).map(
          (c) => c.rootCat_ID ?? c.rC_ID
        ),
        selectedModifierGroups: (selectors.selectedModifierGroups || []).map(
          (g) => g.modifierGroupID ?? g.ModifierGroupID
        ),
        selectedSuppliers: (selectors.selectedSuppliers || []).map(
          (s) => s.s_ID ?? s.S_ID
        ),
        selectedAllergens: (selectors.selectedAllergens || []).map(
          (a) => a.allergen_ID ?? a.allergen_Name
        ),
        selectedMenuGroups: (selectors.selectedMenuGroups || []).map(
          (g) => g.menuGroupID ?? g.MenuGroupID
        ),
        selectedProducts: (selectors.selectedProducts || []).map((p) => ({
          id: p.menuItem_ID,
          qty: p.availableQuantity,
        })),
      },
      reportingCategoryId:
        reportingCategory?.rootCat_ID ?? reportingCategory?.rC_ID ?? null,
      expiryDate: expiryDate
        ? typeof expiryDate.toISOString === "function"
          ? expiryDate.toISOString()
          : String(expiryDate)
        : null,
      variants: (variants || []).map((v) => ({
        menuItem_Name: v.menuItem_Name || "",
        menuItem_SalePrice: v.menuItem_SalePrice ?? "",
        menuItem_CostPrice: v.menuItem_CostPrice ?? "",
        menuItem_TaxPercentage: v.menuItem_TaxPercentage ?? "",
        menuItem_DeliveryPrice: v.menuItem_DeliveryPrice ?? "",
        menuItem_TakeAwayPrice: v.menuItem_TakeAwayPrice ?? "",
        menuItem_IsDefaultVariant: Boolean(v.menuItem_IsDefaultVariant),
        menuItem_SKU: v.menuItem_SKU || "",
        menuItem_ServingSize: v.menuItem_ServingSize || "",
        current_Quantity: v.current_Quantity ?? "",
        reorder_Quantity: v.reorder_Quantity ?? "",
        reorder_Level: v.reorder_Level ?? "",
      })),
    });
  }, [
    formState,
    toggles,
    imageState.menuItem_Image,
    imageState.imageFile,
    selectors,
    reportingCategory,
    expiryDate,
    variants,
  ]);

  // Refresh dirty baseline until the user actually edits something.
  // Async defaults (VAT, reporting category, currency) must not count as unsaved.
  const { isDirty, discardBaseline, syncBaselineAfterSave } =
    useCreateFormDirtyBaseline(getFormSnapshot, !hasUserEdited);

  // Keep default s_VAT on load, but once user edits/clears, never overwrite their choice.
  useEffect(() => {
    setVariants((prevVariants) =>
      prevVariants.map((variant) => {
        const next = {
          ...variant,
          reportCat_ID:
            reportingCategory?.rootCat_ID || reportingCategory?.rC_ID
              ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID)
              : 0,
        };

        if (toggles.isTaxExempt) {
          return { ...next, menuItem_TaxPercentage: "", __taxEdited: false };
        }

        const taxEmpty =
          next.menuItem_TaxPercentage == null || next.menuItem_TaxPercentage === "";

        if (
          !next.__taxEdited &&
          taxEmpty &&
          settings.isTaxEnabled &&
          settings.s_VAT
        ) {
          return { ...next, menuItem_TaxPercentage: settings.s_VAT.toString() };
        }

        return next;
      })
    );
  }, [
    reportingCategory,
    toggles.isTaxExempt,
    settings.isTaxEnabled,
    settings.s_VAT,
  ]);

  // Sync main serving size with default variant's serving size on variants change
  useEffect(() => {
    if (toggles.menuItem_HasVariant) {
      const defaultVariant = variants.find(v => v.menuItem_IsDefaultVariant) || variants[0];
      setFormState(prev => ({
        ...prev,
        menuItem_ServingSize: defaultVariant?.menuItem_ServingSize || "",
        menuItem_CaloriesPerServing:
          defaultVariant?.menuItem_CaloriesPerServing != null &&
          defaultVariant?.menuItem_CaloriesPerServing !== ""
            ? String(defaultVariant.menuItem_CaloriesPerServing)
            : "",
      }));
    }
  }, [variants, toggles.menuItem_HasVariant]);

  const handleInputChange = (field) => (e) => {
    markUserEdited();
    let value = e.target.value;
    
    // Handle price fields: if current value is "0" and user starts typing, clear it
    if (["menuItem_SalePrice", "menuItem_TakeAwayPrice", "menuItem_DeliveryPrice", "menuItem_CostPrice"].includes(field)) {
      const currentValue = formState[field];
      // If current value is "0" and user is typing a digit, replace "0" with the new value
      if (currentValue === "0" && value.length > 0 && /^\d/.test(value)) {
        value = value.replace(/^0+/, "") || "";
      }
    }
    
    setFormState((prev) => ({ ...prev, [field]: value }));

    if (field === "menuItem_Name") {
      setErrors((prev) => ({
        ...prev,
        menuItem_Name: value.trim() ? "" : "Item Name is required",
      }));
    }

    if (field === "menuItem_PreparationTime" && value && !/^\d+$/.test(value)) {
      showErrorToast("Preparation Time must be a positive number (e.g., 40)");
      return;
    }

    if (field === "menuItem_CaloriesPerServing" && value && !/^\d+$/.test(value)) {
      showErrorToast("Calories per serving must be a whole number (e.g., 450)");
      return;
    }

    if (field === "menuItem_TaxPercentage" && !toggles.isTaxExempt) {
      if (value && !/^\d*\.?\d*$/.test(value)) {
        showErrorToast("Tax Percentage must be a valid number (e.g., 20)");
        return;
      }

      setHasUserEditedTax(true);
      setFormState((prev) => ({
        ...prev,
        menuItem_TaxPercentage: value,
      }));
    }

    if (field === "menuItem_SalePrice" && value) {
      if (!/^\d*\.?\d*$/.test(value)) {
        showErrorToast("Sale Price must be a valid number (e.g., 100)");
        return;
      }
    }
    if (field === "menuItem_CostPrice" && value && !/^\d*\.?\d*$/.test(value)) {
      showErrorToast("Cost Price must be a valid number (e.g., 10)");
      return;
    }
  };

  const handleProductQuantityChange = (productId, value) => {
    markUserEdited();
    if (value && !/^\d+$/.test(value)) {
      showErrorToast("Item Quantity must be a positive number");
      return;
    }
    if (parseInt(value) <= 0) {
      showErrorToast("Item Quantity must be greater than 0");
      return;
    }

    setSelectors((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.map((product) =>
        product.menuItem_ID === productId
          ? { ...product, availableQuantity: value }
          : product
      ),
    }));
  };

  const handleToggle = (field) => () => {
    markUserEdited();
    setToggles((prev) => {
      const newToggles = { ...prev, [field]: !prev[field] };
      if (field === "productsEnabled" && !newToggles.productsEnabled) {
        setSelectors((prev) => ({ ...prev, selectedProducts: [] }));
      } else if (field === "isTaxExempt") {
        setHasUserEditedTax(false);
        setFormState((prev) => ({
          ...prev,
          menuItem_TaxPercentage: newToggles.isTaxExempt
            ? ""
            : hasUserEditedTax
              ? (prev.menuItem_TaxPercentage ?? "")
              : prev.menuItem_TaxPercentage ||
                (settings.isTaxEnabled && settings.s_VAT
                  ? settings.s_VAT.toString()
                  : ""),
        }));
        // Reset variant tax edited flags when toggling tax exemption.
        setVariants((prevVariants) =>
          prevVariants.map((v) => ({
            ...v,
            __taxEdited: false,
            menuItem_TaxPercentage: newToggles.isTaxExempt ? "" : v.menuItem_TaxPercentage,
          }))
        );
      } else if (field === "menuItem_HasVariant" && newToggles.menuItem_HasVariant) {
        const initialVariant = {
          ...Modifier,
          menuItem_ServingSize: formState.menuItem_ServingSize || "",
          menuItem_CaloriesPerServing: formState.menuItem_CaloriesPerServing
            ? parseInt(formState.menuItem_CaloriesPerServing, 10)
            : null,
          reportCat_ID: reportingCategory?.rootCat_ID || reportingCategory?.rC_ID ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID) : 0,
          __taxEdited: false,
          menuItem_TaxPercentage: !newToggles.isTaxExempt && settings.isTaxEnabled && settings.s_VAT ? settings.s_VAT.toString() : "",
          menuItem_IsDefaultVariant: true,
          current_Quantity: formState.current_Quantity ?? "0",
          reorder_Quantity: formState.reorder_Quantity ?? "0",
          reorder_Level: formState.reorder_Level ?? "0",
        };
        setVariants([initialVariant]);
      }
      return newToggles;
    });
  };

  const handleVariantChange = (index, field, value) => {
    markUserEdited();
    if (field === "menuItem_CaloriesPerServing" && value && !/^\d+$/.test(value)) {
      showErrorToast("Calories per serving must be a whole number (e.g., 450)");
      return;
    }

    // Handle price fields: if current value is "0" and user starts typing, clear it
    if (["menuItem_SalePrice", "menuItem_TakeAwayPrice", "menuItem_DeliveryPrice", "menuItem_CostPrice"].includes(field)) {
      const currentValue = variants[index]?.[field];
      // If current value is "0" or 0 and user is typing a digit, replace "0" with the new value
      if ((currentValue === "0" || currentValue === 0) && value.length > 0 && /^\d/.test(value)) {
        value = value.replace(/^0+/, "") || "";
      }
    }
    
    let updatedVariants = variants.map((variant, i) =>
      i === index
        ? {
            ...variant,
            [field]: value,
            ...(field === "menuItem_TaxPercentage" ? { __taxEdited: true } : {}),
          }
        : variant
    );
    if (field === "menuItem_IsDefaultVariant" && value === true) {
      updatedVariants = updatedVariants.map((variant, i) => ({
        ...variant,
        menuItem_IsDefaultVariant: i === index ? true : false,
      }));

      const defaultVariant = updatedVariants[index];

      setFormState((prev) => ({
        ...prev,
        menuItem_SalePrice: defaultVariant.menuItem_SalePrice || "0.00",
        menuItem_DeliveryPrice: defaultVariant.menuItem_DeliveryPrice || "0.00",
        menuItem_TakeAwayPrice: defaultVariant.menuItem_TakeAwayPrice || "0.00",
        menuItem_TaxPercentage: defaultVariant.menuItem_TaxPercentage || "0.00",
        menuItem_CostPrice: defaultVariant.menuItem_CostPrice || "0.00",
        menuItem_ServingSize: defaultVariant.menuItem_ServingSize || "",
        menuItem_CaloriesPerServing:
          defaultVariant.menuItem_CaloriesPerServing != null &&
          defaultVariant.menuItem_CaloriesPerServing !== ""
            ? String(defaultVariant.menuItem_CaloriesPerServing)
            : "",
      }));
    }

    setVariants(updatedVariants);

    // Sync main serving size if this variant is default and field is serving size
    if (field === "menuItem_ServingSize" && updatedVariants[index].menuItem_IsDefaultVariant) {
      setFormState((prev) => ({
        ...prev,
        menuItem_ServingSize: value,
      }));
    }

    if (field === "menuItem_CaloriesPerServing" && updatedVariants[index].menuItem_IsDefaultVariant) {
      setFormState((prev) => ({
        ...prev,
        menuItem_CaloriesPerServing: value,
      }));
    }

    if (field === "menuItem_Name" && value && index === variants.length - 1) {
      setVariants([
        ...updatedVariants,
        {
          ...Modifier,
          menuItem_ServingSize: "",
          reportCat_ID:
            reportingCategory?.rootCat_ID || reportingCategory?.rC_ID
              ? parseInt(
                  reportingCategory.rootCat_ID || reportingCategory.rC_ID
                )
              : 0,
          __taxEdited: false,
          menuItem_TaxPercentage:
            !toggles.isTaxExempt && settings.isTaxEnabled && settings.s_VAT
              ? settings.s_VAT.toString()
              : "",
        },
      ]);
    }
  };

  const handleRemoveVariant = (index) => {
    markUserEdited();
    if (variants.length === 1) {
      showErrorToast("At least one variant is required");
      return;
    }
    setVariants(variants.filter((_, i) => i !== index));
  };

  const parseVariantExpiryToDate = (variant) => {
    const raw = variant?.menuItem_ExpiryDate;
    if (!raw || raw === "0001-01-01T00:00:00") return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const normalizeVariantForDetailDraft = (variant) => ({
    ...variant,
    menuItem_Name: variant.menuItem_Name ?? "",
    menuItem_KitchenDisplayName: variant.menuItem_KitchenDisplayName ?? "",
    menuItem_ServingSize: variant.menuItem_ServingSize ?? "",
    menuItem_CaloriesPerServing:
      variant.menuItem_CaloriesPerServing != null && variant.menuItem_CaloriesPerServing !== ""
        ? String(variant.menuItem_CaloriesPerServing)
        : "",
    menuItem_SKU: variant.menuItem_SKU ?? "",
  });

  const openVariantDetailModal = (index) => {
    const variant = variants[index];
    if (!variant) return;
    const { __taxEdited, ...rest } = variant;
    setVariantDetailDraft(normalizeVariantForDetailDraft(rest));
    setVariantDetailExpiry(parseVariantExpiryToDate(variant));
    setVariantDetailIndex(index);
    setVariantDetailModalOpen(true);
  };

  const closeVariantDetailModal = () => {
    setVariantDetailModalOpen(false);
    setVariantDetailIndex(null);
    setVariantDetailDraft(null);
    setVariantDetailExpiry(null);
  };

  const updateVariantDetailDraft = (field, value) => {
    setVariantDetailDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const setVariantDetailPublish = (checked) => {
    setVariantDetailDraft((prev) => {
      if (!prev) return prev;
      if (checked) {
        return { ...prev, menuItem_Publish: true };
      }
      return { ...prev, menuItem_Publish: false };
    });
  };

  const saveVariantDetailModal = () => {
    if (variantDetailIndex == null || variantDetailDraft == null) return;

    if (
      variantDetailDraft.menuItem_CaloriesPerServing &&
      !/^\d+$/.test(String(variantDetailDraft.menuItem_CaloriesPerServing))
    ) {
      showErrorToast("Calories per serving must be a whole number (e.g., 450)");
      return;
    }

    markUserEdited();
    const expiryIso = variantDetailExpiry ? variantDetailExpiry.toISOString() : null;

    setVariants((prev) => {
      const current = prev[variantDetailIndex];
      const taxChanged =
        String(variantDetailDraft.menuItem_TaxPercentage ?? "") !==
        String(current?.menuItem_TaxPercentage ?? "");

      let next = prev.map((row, i) =>
        i === variantDetailIndex
          ? {
              ...row,
              ...variantDetailDraft,
              menuItem_ExpiryDate: expiryIso,
              __taxEdited: row.__taxEdited || taxChanged,
            }
          : row
      );

      if (variantDetailDraft.menuItem_IsDefaultVariant) {
        next = next.map((row, i) => ({
          ...row,
          menuItem_IsDefaultVariant: i === variantDetailIndex,
        }));

        const defaultVariant = next[variantDetailIndex];
        setFormState((prev) => ({
          ...prev,
          menuItem_SalePrice: defaultVariant.menuItem_SalePrice || "0.00",
          menuItem_DeliveryPrice: defaultVariant.menuItem_DeliveryPrice || "0.00",
          menuItem_TakeAwayPrice: defaultVariant.menuItem_TakeAwayPrice || "0.00",
          menuItem_TaxPercentage: defaultVariant.menuItem_TaxPercentage || "0.00",
          menuItem_CostPrice: defaultVariant.menuItem_CostPrice || "0.00",
          menuItem_ServingSize: defaultVariant.menuItem_ServingSize || "",
          menuItem_CaloriesPerServing:
            defaultVariant.menuItem_CaloriesPerServing != null &&
            defaultVariant.menuItem_CaloriesPerServing !== ""
              ? String(defaultVariant.menuItem_CaloriesPerServing)
              : "",
        }));
      }

      return next;
    });

    closeVariantDetailModal();
  };

  const handleVariantRowClick = (e, index) => {
    if (
      e.target.closest(
        "input, textarea, select, button, .toggle-switch, .switch, .slider, label.switch, .fa-trash, .fa-bars, .fa-eye"
      )
    ) {
      return;
    }
    openVariantDetailModal(index);
  };

  const variantModalSectionStyle = {
    backgroundColor: isDarkMode ? "#1a202c" : "#f8f9fa",
    border: isDarkMode ? "1px solid #4a5568" : "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "16px",
  };

  const renderVariantDetailModal = () => {
    if (!variantDetailDraft) return null;
    const d = variantDetailDraft;

    return (
      <CustomModal
        title="Variant details"
        subtitle={
          d.menuItem_Name?.trim()
            ? `Editing: ${d.menuItem_Name.trim()}`
            : "Edit pricing, SKU, expiry, and stock"
        }
        fontSize="18px"
        fontWeight={600}
        isOpen={variantDetailModalOpen}
        onClose={closeVariantDetailModal}
        onSubmit={saveVariantDetailModal}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText="Save"
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        minHeight="360px"
      >
        <div className="container-fluid px-0">
          <div style={variantModalSectionStyle}>
            <h6 className="fw-semibold mb-3" style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}>
              Variant
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <FormInput
                  label="Variant name"
                  type="text"
                  value={d.menuItem_Name || ""}
                  placeHolder="Enter variant name"
                  onchange={(e) => updateVariantDetailDraft("menuItem_Name", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Kitchen name"
                  type="text"
                  value={d.menuItem_KitchenDisplayName || ""}
                  placeHolder="Kitchen display name"
                  onchange={(e) =>
                    updateVariantDetailDraft("menuItem_KitchenDisplayName", e.target.value)
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Serving size"
                  type="text"
                  value={d.menuItem_ServingSize || ""}
                  placeHolder="e.g. 250g or 2 slices"
                  onchange={(e) => updateVariantDetailDraft("menuItem_ServingSize", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Calories per serving"
                  type="number"
                  value={d.menuItem_CaloriesPerServing ?? ""}
                  placeHolder="e.g. 450"
                  onchange={(e) =>
                    updateVariantDetailDraft("menuItem_CaloriesPerServing", e.target.value)
                  }
                />
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <ToggleSwitch
                  label="Published"
                  isChecked={!!d.menuItem_Publish}
                  onToggle={setVariantDetailPublish}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </div>

          <div style={variantModalSectionStyle}>
            <h6 className="fw-semibold mb-3" style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}>
              Pricing
            </h6>
            <div className="row g-3">
              <div className="col-md-4">
                <FormInput
                  label={`Cost price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_CostPrice !== undefined &&
                    d.menuItem_CostPrice !== null &&
                    d.menuItem_CostPrice !== ""
                      ? String(d.menuItem_CostPrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) => updateVariantDetailDraft("menuItem_CostPrice", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="Tax %"
                  type="number"
                  value={d.menuItem_TaxPercentage == null ? "" : String(d.menuItem_TaxPercentage)}
                  placeHolder={
                    settings?.isTaxEnabled && settings?.s_VAT && !toggles.isTaxExempt
                      ? settings.s_VAT.toString()
                      : "0"
                  }
                  step="0.01"
                  readOnly={toggles.isTaxExempt}
                  onchange={(e) => updateVariantDetailDraft("menuItem_TaxPercentage", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label={`Sale price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_SalePrice != null &&
                    d.menuItem_SalePrice !== "" &&
                    d.menuItem_SalePrice !== 0 &&
                    d.menuItem_SalePrice !== "0"
                      ? String(d.menuItem_SalePrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) => updateVariantDetailDraft("menuItem_SalePrice", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label={`Delivery price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_DeliveryPrice != null &&
                    d.menuItem_DeliveryPrice !== "" &&
                    d.menuItem_DeliveryPrice !== 0 &&
                    d.menuItem_DeliveryPrice !== "0"
                      ? String(d.menuItem_DeliveryPrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) => updateVariantDetailDraft("menuItem_DeliveryPrice", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label={`Takeaway price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_TakeAwayPrice != null &&
                    d.menuItem_TakeAwayPrice !== "" &&
                    d.menuItem_TakeAwayPrice !== 0 &&
                    d.menuItem_TakeAwayPrice !== "0"
                      ? String(d.menuItem_TakeAwayPrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) => updateVariantDetailDraft("menuItem_TakeAwayPrice", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div style={variantModalSectionStyle}>
            <h6 className="fw-semibold mb-3" style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}>
              SKU and expiry
            </h6>
            <div className="row g-3 align-items-start">
              <div className="col-md-6 d-flex flex-column">
                <label className="form-label fw-semibold mb-2" style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}>
                  SKU
                </label>
                <FormInput
                  type="text"
                  value={d.menuItem_SKU || ""}
                  placeHolder="SKU code"
                  onchange={(e) => updateVariantDetailDraft("menuItem_SKU", e.target.value)}
                />
              </div>
              <div className="col-md-6 d-flex flex-column">
                <label className="form-label fw-semibold mb-2" style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}>
                  Expiry date
                </label>
                <div style={{ width: "100%", minHeight: "44px" }}>
                  <DebugMUIDatePicker
                    value={variantDetailExpiry}
                    onChange={(date) => setVariantDetailExpiry(date)}
                    placeholder="Select expiry date"
                    name="variantDetailExpiry"
                    clearable={true}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ ...variantModalSectionStyle, marginBottom: 0 }}>
            <h6 className="fw-semibold mb-3" style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}>
              Inventory
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <FormInput
                  label="Current quantity"
                  type="number"
                  value={d.current_Quantity ?? ""}
                  placeHolder="0"
                  onchange={(e) => updateVariantDetailDraft("current_Quantity", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Reorder quantity"
                  type="number"
                  value={d.reorder_Quantity ?? ""}
                  placeHolder="0"
                  onchange={(e) => updateVariantDetailDraft("reorder_Quantity", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Reorder level"
                  type="number"
                  value={d.reorder_Level ?? ""}
                  placeHolder="0"
                  onchange={(e) => updateVariantDetailDraft("reorder_Level", e.target.value)}
                />
              </div>
            </div>
            <div className="mt-3">
              <ToggleSwitch
                label="Default variant"
                isChecked={!!d.menuItem_IsDefaultVariant}
                onToggle={(checked) =>
                  updateVariantDetailDraft("menuItem_IsDefaultVariant", checked)
                }
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    );
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    markUserEdited();
    e.preventDefault();
    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;
    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const newVariants = [...variants];
    const draggedVariant = newVariants[draggedIndex];
    newVariants.splice(draggedIndex, 1);
    newVariants.splice(overIndex, 0, draggedVariant);
    setVariants(newVariants);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const disablePastDates = (current) => {
    return !current.isSameOrAfter(moment().startOf("day"));
  };

  const handleASTimeChange = (time) => {
    markUserEdited();
    const formattedTime = moment(time).format("HH:mm");
    setFormState((prev) => ({ ...prev, menuItem_ASTime: formattedTime }));
  };

  const handleAETimeChange = (time) => {
    markUserEdited();
    const formattedTime = moment(time).format("HH:mm");
    setFormState((prev) => ({ ...prev, menuItem_AETime: formattedTime }));
  };

  const handleProductTypeSelect = (type) => {
    markUserEdited();
    setFormState((prev) => ({ ...prev, menuItem_RootType: type }));
    closeAllModals();
  };

  const handleReportingCategorySubmit = (categoryData) => {
    markUserEdited();
    setReportingCategory(categoryData);
    closeAllModals();
  };

  const refreshReportingCategoriesFromTree = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
      const tree = response.data.data || response.data;
      if (!Array.isArray(tree)) return;
      const flat = flattenCategoryTreeForSync(tree);
      setSelectors((prev) => ({
        ...prev,
        selectedParentCategory: prev.selectedParentCategory.map((sel) => {
          const id = sel?.rootCat_ID ?? sel?.rC_ID;
          if (id == null) return sel;
          const match = flat.find(
            (c) =>
              String(c?.rootCat_ID) === String(id) ||
              String(c?.rC_ID) === String(id)
          );
          return match ? { ...sel, ...match } : sel;
        }),
      }));
    } catch (err) {
      showErrorToast("Could not refresh categories.");
    }
  }, [CompID]);

  const removeProduct = (productId) => {
    markUserEdited();
    setSelectors((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter(
        (p) => p.menuItem_ID !== productId
      ),
    }));
  };

  const handleSelectorSubmit = (key) => (selectedItems) => {
    markUserEdited();
    if (key === "selectedProducts") {
      const updatedItems = selectedItems.map((item) => ({
        ...item,
        availableQuantity: item.availableQuantity || "1",
      }));
      setSelectors((prev) => ({ ...prev, [key]: updatedItems }));
    } else {
      const nextItems = Array.isArray(selectedItems) ? selectedItems : [];
      if (key === "selectedAllergens") {
        setSelectors((prev) => ({
          ...prev,
          selectedAllergens: resolveSelectedAllergens(nextItems, allAllergens),
        }));
      } else {
        setSelectors((prev) => ({ ...prev, [key]: nextItems }));
      }
      if (key === "selectedParentCategory") {
        setReportingCategory((prev) =>
          syncReportingCategoryWithSelection(nextItems, prev)
        );
      }
    }
    closeAllModals();
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!formState.menuItem_Name.trim()) {
      setErrors((prev) => ({
        ...prev,
        menuItem_Name: "Name is required",
      }));
      showErrorToast("Name is required.");
      return false;
    }

    if (toggles.menuItem_HasVariant) {
      const validVariants = variants.filter((v) => v.menuItem_Name.trim());
      if (validVariants.length === 0) {
        showErrorToast(
          "At least one variant is required when variants are enabled."
        );
        return false;
      }
      for (const variant of validVariants) {
        if (parseFloat(variant.menuItem_SalePrice) <= 0) {
          showErrorToast("Variant Sale Price must be a positive number.");
          return false;
        }
      }
    } else {
      if (parseFloat(formState.menuItem_SalePrice) <= 0) {
        setErrors((prev) => ({
          ...prev,
          menuItem_SalePrice: "Sale Price must be a positive number",
        }));
        showErrorToast("Sale Price must be a positive number.");
        return false;
      }
    }

    if (selectors.selectedParentCategory.length === 0) {
      showErrorToast("At least one category must be selected.");
      return false;
    }

    if (!reportingCategory?.rootCat_ID && !reportingCategory?.rC_ID) {
      showErrorToast("Reporting Category is required.");
      return false;
    }

    if (toggles.productsEnabled && selectors.selectedProducts.length === 0) {
      showErrorToast("At least one product must be selected for a combo item.");
      return false;
    }

    if (
      toggles.productsEnabled &&
      selectors.selectedProducts.some(
        (product) =>
          !product.availableQuantity || !/^\d+$/.test(product.availableQuantity)
      )
    ) {
      showErrorToast("All selected products must have a valid quantity.");
      return false;
    }

    if (toggles.allergensEnabled && selectors.selectedAllergens.length === 0) {
      showErrorToast(
        "At least one allergen must be selected when allergens are enabled."
      );
      return false;
    }

    setLoading(true);

    let uploadedImageUrl = imageState.menuItem_Image;
    try {
      if (imageState.imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("ImageFile", imageState.imageFile);
        const imageResponse = await axiosInstance.post(
          APIs.POST_UPLOAD_PRODUCT_IMAGE,
          imageFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        uploadedImageUrl =
          imageResponse.data?.data?.filePath || imageState.menuItem_Image;
      }
    } catch (err) {
      showErrorToast("Failed to upload image");
    }

    try {
      const normalizedModifierGroups = toggles.modifierGroupsEnabled
        ? (selectors.selectedModifierGroups || [])
            .map((g) => ({
              ModifierGroupID: Number(
                g?.ModifierGroupID ?? g?.modifierGroupID ?? g?.modifierGroupId ?? 0
              ),
              Name: g?.Name ?? g?.name ?? "",
              // Backend model uses non-nullable strings here; send empty string to avoid 400 validation.
              SubTitle: g?.SubTitle ?? g?.subTitle ?? "",
              Detail: g?.Detail ?? g?.detail ?? "",
              CompID: Number(g?.CompID ?? g?.compID ?? CompID ?? 0),
            }))
            .filter((g) => Number.isFinite(g.ModifierGroupID) && g.ModifierGroupID > 0)
        : [];

      const defaultVariant =
        (variants || []).find((v) => v.menuItem_IsDefaultVariant) ||
        (variants || [])[0];
      const parentName = (formState.menuItem_Name || "").trim();
      // Parent stores its own name; each variant stores "Parent (Variant)"
      const defaultVariantTitle = toggles.menuItem_HasVariant ? parentName : "";
      const payload = {
        menuItem_ID: 0,
        localizeID: formState.localizeID,
        compID: CompID,
        menuItem_Name: formState.menuItem_Name,
        menuItem_KitchenDisplayName: formState.menuItem_KitchenDisplayName,
        menuItem_Detail: formState.menuItem_Detail,
        menuItem_SalePrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_SalePrice) || 0
          : parseFloat(formState.menuItem_SalePrice) || 0,
        menuItem_Image: uploadedImageUrl || "",
        menuItem_Publish: toggles.menuItem_Publish,
        menuItem_DataCreated: new Date().toISOString(),
        menuItem_ExpiryDate: toggles.menuItem_HasVariant
          ? null
          : expiryDate
            ? expiryDate.toISOString()
            : null,
        menuItem_DateModified: new Date().toISOString(),
        menuItem_DeliveryPrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_DeliveryPrice) || 0
          : parseFloat(formState.menuItem_DeliveryPrice) || 0,
        menuItem_TakeAwayPrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_TakeAwayPrice) || 0
          : parseFloat(formState.menuItem_TakeAwayPrice) || 0,
        menuItem_CostPrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_CostPrice) || 0
          : parseFloat(formState.menuItem_CostPrice) || 0,
        menuItem_TaxPercentage: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_TaxPercentage) || 0
          : parseFloat(formState.menuItem_TaxPercentage) || 0,
        menuItem_SKU: toggles.skuEnabled ? formState.menuItem_SKU : "",
        menuItem_RootType: formState.menuItem_RootType,
        menuItem_SEOTitle: toggles.seoEnabled
          ? formState.menuItem_SEOTitle
          : "",
        menuItem_SEODescription: toggles.seoEnabled
          ? formState.menuItem_SEODescription
          : "",
        menuItem_Permalink: toggles.seoEnabled
          ? formState.menuItem_Permalink
          : "",
        isActiveMobileApp: toggles.isActiveMobileApp,
        is_OutOfStock: toggles.is_OutOfStock,
        isSellOnTill: toggles.isSellOnTill,
        is_ShowInKiosk: toggles.is_ShowInKiosk,
        hasAllergen:
          toggles.allergensEnabled && selectors.selectedAllergens.length > 0,
        menuItem_HaveModifier:
          normalizedModifierGroups.length > 0,
        categories: selectors.selectedParentCategory,
        suppliers: toggles.suppliersEnabled ? selectors.selectedSuppliers : [],
        modifierGroups: normalizedModifierGroups,
        menuGroups: selectors.selectedMenuGroups,
        allergens:
          toggles.allergensEnabled && selectors.selectedAllergens.length > 0
            ? selectors.selectedAllergens.map((a) => a.allergen_Name).join(", ")
            : "",
        allergenSolutions:
          toggles.allergensEnabled && selectors.selectedAllergens.length > 0
            ? joinAllergenSolutions(selectors.selectedAllergens)
            : "",
        menuItem_PreparationTime: formState.menuItem_PreparationTime.trim(),
        menuItem_ServingSize: formState.menuItem_ServingSize.trim(),
        menuItem_CaloriesPerServing: formState.menuItem_CaloriesPerServing
          ? parseInt(formState.menuItem_CaloriesPerServing, 10)
          : null,
        menuItem_ASTime: formState.menuItem_ASTime || "",
        menuItem_AETime: formState.menuItem_AETime || "",
        menuItem_Rating: parseInt(formState.menuItem_Rating) || 0,
        menuItem_Homefeatured: toggles.menuItem_Homefeatured,
        menuItem_featured: toggles.menuItem_featured,
        menuItem_IsModifier: toggles.menuItem_IsModifier,
        menuItem_ModifierType: toggles.menuItem_IsModifier
          ? formState.menuItem_ModifierType || "0"
          : "0",
        menuItem_IsFavorite: toggles.menuItem_IsFavorite,
        menuItem_BarCode: formState.menuItem_BarCode || "",
        menuItem_Type: formState.menuItem_Type,
        menuItem_ColorCode: formState.menuItem_ColorCode || "",
        retail_Markup: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.retail_Markup) || 0,
        delivery_Markup: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.delivery_Markup) || 0,
        online_Markup: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.online_Markup) || 0,
        current_Quantity: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.current_Quantity) || 0,
        reorder_Quantity: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.reorder_Quantity) || 0,
        reorder_Level: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.reorder_Level) || 0,
        availableQuantity: toggles.menuItem_HasVariant
          ? null
          : parseFloat(formState.availableQuantity) || null,
        orderQuantityLimit: toggles.menuItem_HasVariant
          ? 0
          : parseInt(formState.orderQuantityLimit) || 0,
        brand_ID: parseInt(formState.brand_ID) || 0,
        ingredientID: parseInt(formState.ingredientID) || 0,
        parentMenuItemID: parseInt(formState.parentMenuItemID) || 0,
        itsMasterProductID: parseInt(formState.itsMasterProductID) || 0,
        reportCat_ID:
          reportingCategory?.rootCat_ID || reportingCategory?.rC_ID
            ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID)
            : 0,
        stadardUnit: toggles.menuItem_HasVariant
          ? 0
          : parseInt(formState.stadardUnit) || 0,
        unitSize: toggles.menuItem_HasVariant
          ? 0
          : parseInt(formState.unitSize) || 0,
        mP_UnitOfSale: formState.mP_UnitOfSale || "",
        mP_VolumnOfSale: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.mP_VolumnOfSale) || 0,
        menuItem_HasVariant: toggles.menuItem_HasVariant,
        isArchieve: toggles.isArchieve,
        isAllowedtoSetPriceatThePointofSale:
          toggles.isAllowedtoSetPriceatThePointofSale,
        isTaxExempt: toggles.isTaxExempt,
        isPreventGainingLoyalty: toggles.isPreventGainingLoyalty,
        isHaveCustomerSpecificPricing: toggles.isHaveCustomerSpecificPricing,
        isProductisScannableOnly: toggles.isProductisScannableOnly,
        isMasterProduct: toggles.isMasterProduct,
        itHasAMasterProduct: toggles.itHasAMasterProduct,
        menuItem_IsDefaultModifier: toggles.menuItem_IsDefaultModifier,
        menuItem_DefaultModifierTitle:
          formState.menuItem_DefaultModifierTitle || "",
        isShowNotificationExpDate: toggles.isShowNotificationExpDate,
        isShowSocialMediaLink: toggles.isShowSocialMediaLink,
        menuItem_ImageAltText: formState.menuItem_ImageAltText || "",
        appMode: toggles.menuItem_HasVariant ? "" : formState.appMode,
        menuItem_IsVariant: false,
        menuItem_IsDefaultVariant:
          variants[0]?.menuItem_IsDefaultVariant || false,
        menuItem_DefaultVariantTitle: defaultVariantTitle,
        menuitem_VariantParentID: parseInt(formState.parentMenuItemID) || 0,
        Products: IsCombo
          ? selectors.selectedProducts
          : toggles.menuItem_HasVariant
          ? variants
              .filter(
                (v) => v.menuItem_Name && v.menuItem_Name.trim().length > 0
              )
              .map((v) => {
                const { __taxEdited, ...rest } = v;
                const taxVal = rest.menuItem_TaxPercentage;
                const parsedTax = parseFloat(taxVal);
                const menuItem_TaxPercentage =
                  taxVal === "" || taxVal == null
                    ? null
                    : Number.isFinite(parsedTax)
                      ? parsedTax
                      : null;
                const variantName = (v.menuItem_Name || "").trim();

                return {
                  ...rest,
                reportCat_ID:
                  reportingCategory?.rootCat_ID || reportingCategory?.rC_ID
                    ? parseInt(
                        reportingCategory.rootCat_ID || reportingCategory.rC_ID
                      )
                    : 0,
                menuItem_ServingSize: v.menuItem_ServingSize || "",
                menuItem_CaloriesPerServing: v.menuItem_CaloriesPerServing
                  ? parseInt(v.menuItem_CaloriesPerServing, 10)
                  : null,
                menuItem_ExpiryDate: v.menuItem_ExpiryDate || null,
                hasAllergen: false,
                allergens: "",
                allergenSolutions: "",
                appMode: "",
                  menuItem_TaxPercentage,
                  // Variants store "Parent (Variant)" for display / reporting
                  menuItem_DefaultVariantTitle:
                    parentName && variantName
                      ? `${parentName} (${variantName})`
                      : "",
                };
              })
          : [],
      };

      const response = await axiosInstance.post(
        APIs.POST_CREATE_MENU_ITEM,
        payload
      );
      
      if (response.data.status) {
        showSuccessToast("Item created successfully");
        syncBaselineAfterSave();
        setHasUserEdited(false);
        const newId =
          response.data?.data?.menuItem_ID ??
          response.data?.data?.MenuItem_ID ??
          response.data?.data;
        if (exitAfterSave) {
          navigate("/items/manageItems");
        } else if (newId && !Number.isNaN(Number(newId)) && Number(newId) > 0) {
          navigate(`/items/updateItem/${newId}`);
        } else {
          navigate("/items/manageItems");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to create item");
        return false;
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to create item");
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      discardBaseline();
      setHasUserEdited(false);
    },
  });

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/items/manageItems">Manage Items</Link>
          </li>
          <li className="breadcrumb-item active">Add Item</li>
        </ol>
        <h1 className="page-header mb-0">
          {IsCombo ? "Add Combo Item" : "Add Item"}
        </h1>
      </div>
      <SaveActions
        onCancel={() => navigate("/items/manageItems")}
        onSave={() => handleSubmit({ exitAfterSave: false })}
        onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
        saving={loading}
        disabled={loading}
      />
    </div>
  );

  const renderToggles = () => (
    <div className="row row-cols-2 g-3">
      {[
        { label: "Active", field: "menuItem_Publish" },
        { label: "Show in Kiosk", field: "is_ShowInKiosk" },
        { label: "Show in POS", field: "isSellOnTill" },
        { label: "Tax Exempt", field: "isTaxExempt" },
        { label: "Show in Online", field: "isActiveMobileApp" },
        { label: "Out of Stock", field: "is_OutOfStock" },
      ].map(({ label, field }) => (
        <div key={field} className="col">
          <ToggleSwitch
            isChecked={toggles[field]}
            onToggle={handleToggle(field)}
            label={label}
            name={field}
            themeColor={themeColor}
          />
        </div>
      ))}
    </div>
  );

  const renderFormFields = () => (
    <div className="row g-4">
      <div className="col-md-6">
        <FormInput
          label={IsCombo ? "Combo Name *" : "Item Name *"}
          value={formState.menuItem_Name}
          onchange={handleInputChange("menuItem_Name")}
          type="text"
          placeHolder="Name"
          name="menuItem_Name"
          required
          error={errors.menuItem_Name}
        />
        <FormInput
          label={IsCombo ? "Combo Kitchen Name " : "Kitchen Name "}
          value={formState.menuItem_KitchenDisplayName}
          onchange={handleInputChange("menuItem_KitchenDisplayName")}
          type="text"
          placeHolder="Kitchen Name"
          name="menuItem_KitchenDisplayName"
        />
        <div className="mb-4">
          <label className="form-label fw-medium">
            <b>{IsCombo ? "Combo Description" : "Item Description"}</b>
          </label>
          <textarea
            className={`form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`}
            rows={6}
            placeholder="Description (max 500 characters)"
            value={formState.menuItem_Detail}
            onChange={handleInputChange("menuItem_Detail")}
            style={{
              borderRadius: "10px",
              border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
              background: isDarkMode ? "#2d3436" : "#F9F9F9",
              color: isDarkMode ? "#ffffff" : "#000000",
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            }}
          />
        </div>
        {!IsCombo && (
          <div className="mb-3">
            <ToggleSwitch
              isChecked={toggles.menuItem_HasVariant}
              onToggle={handleToggle("menuItem_HasVariant")}
              label="Is Product have multiple variants"
              name="menuItem_HasVariant"
              themeColor={themeColor}
            />
          </div>
        )}
        {!toggles.menuItem_HasVariant && (
          <div 
            className="card p-3 mb-4"
            style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
              color: isDarkMode ? '#ffffff' : '#000000',
            }}
          >
            <h5 className="card-title mb-3">Pricing</h5>
            <div className="row g-3">
              <div className="col-6">
                <FormInput
                  label={`Cost Price (${getCurrencySymbol(compCurrency)})`}
                  value={formState.menuItem_CostPrice}
                  onchange={handleInputChange("menuItem_CostPrice")}
                  type="number"
                  placeHolder="0.00"
                  name="menuItem_CostPrice"
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Tax (Percentage)"
                  value={formState.menuItem_TaxPercentage}
                  onchange={handleInputChange("menuItem_TaxPercentage")}
                  type="text"
                  placeHolder="0.00"
                  name="menuItem_TaxPercentage"
                  disabled={toggles.isTaxExempt}
                  title={
                    formState.menuItem_TaxPercentage &&
                    settings.s_VAT &&
                    formState.menuItem_TaxPercentage ===
                      settings.s_VAT.toString()
                      ? "Default tax rate from settings - you can edit this value"
                      : "Enter tax percentage"
                  }
                />
              </div>
              <div className="col-6">
                <FormInput
                  label={`Sale Price * (${getCurrencySymbol(compCurrency)})`}
                  value={formState.menuItem_SalePrice}
                  onchange={handleInputChange("menuItem_SalePrice")}
                  type="number"
                  placeHolder="0.00"
                  name="menuItem_SalePrice"
                  required
                  error={errors.menuItem_SalePrice}
                />
              </div>
            </div>
            {!toggles.menuItem_HasVariant && (
              <>
                <h5 className="card-title mt-3">Additional Pricing</h5>
                <div className="row g-3 mt-2">
                  <div className="col-6">
                    <FormInput
                      label={`Takeaway Price (${getCurrencySymbol(compCurrency)})`}
                      value={formState.menuItem_TakeAwayPrice}
                      onchange={handleInputChange("menuItem_TakeAwayPrice")}
                      type="number"
                      placeHolder="0.00"
                      name="menuItem_TakeAwayPrice"
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label={`Delivery Price (${getCurrencySymbol(compCurrency)})`}
                      value={formState.menuItem_DeliveryPrice}
                      onchange={handleInputChange("menuItem_DeliveryPrice")}
                      type="number"
                      placeHolder="0.00"
                      name="menuItem_DeliveryPrice"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <div className="col-md-6">
        <div className="row mb-2">
          <div className="col-md-12">
            <FormInput
              label="Product Type"
              value={formState.menuItem_RootType}
              onchange={handleInputChange("menuItem_RootType")}
              type="text"
              placeHolder="Select Product Type"
              name="menuItem_RootType"
              icon="fa fa-caret-down"
              iconOnClick={() => openModal("isModalOpen")}
            />
          </div>
          {/* <div className="col-md-12">
            <FormInput
              label="Color Code"
              value={formState.menuItem_ColorCode}
              onchange={handleInputChange("menuItem_ColorCode")}
              type="color"
              name="menuItem_ColorCode"
            />
          </div> */}
        </div>
        <div className="row g-3 mb-3">
          <div className={toggles.menuItem_HasVariant ? "col-12" : "col-6"}>
            <FormInput
              label="Preparation Time"
              value={formState.menuItem_PreparationTime}
              onchange={handleInputChange("menuItem_PreparationTime")}
              type="text"
              placeHolder="00:00"
              name="menuItem_PreparationTime"
            />
          </div>

          {!toggles.menuItem_HasVariant && (
            <>
              <div className="col-6">
                <FormInput
                  label="Serving Size"
                  value={formState.menuItem_ServingSize}
                  onchange={handleInputChange("menuItem_ServingSize")}
                  type="text"
                  placeHolder="e.g. 250g or 2 slices"
                  name="menuItem_ServingSize"
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Calories per serving"
                  value={formState.menuItem_CaloriesPerServing}
                  onchange={handleInputChange("menuItem_CaloriesPerServing")}
                  type="number"
                  placeHolder="e.g. 450"
                  name="menuItem_CaloriesPerServing"
                />
              </div>
            </>
          )}
        </div>
        {!toggles.menuItem_HasVariant && (
          <div className="col-12 mb-3">
            <div 
              className="card p-3 mb-4"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              <h5 className="card-title mb-3">Inventory</h5>
              <div className="row g-3">
                <div className="col-6">
                  <FormInput
                    label="Current Quantity"
                    value={formState.current_Quantity}
                    onchange={handleInputChange("current_Quantity")}
                    type="number"
                    placeHolder="0"
                    name="current_Quantity"
                  />
                </div>
                <div className="col-6">
                  <FormInput
                    label="Reorder Quantity"
                    value={formState.reorder_Quantity}
                    onchange={handleInputChange("reorder_Quantity")}
                    type="number"
                    placeHolder="0"
                    name="reorder_Quantity"
                  />
                </div>
                <div className="col-6">
                  <FormInput
                    label="Reorder Level"
                    value={formState.reorder_Level}
                    onchange={handleInputChange("reorder_Level")}
                    type="number"
                    placeHolder="0"
                    name="reorder_Level"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {!IsCombo && toggles.menuItem_HasVariant && (
        <div className="row">
          <div className="col-12 mt-4">
            <div 
              className="card p-3 mb-4"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              <h5 className="card-title mb-3">Variants</h5>
              <table className="table table-responsive">
                <thead style={{ 
                  backgroundColor: isDarkMode ? "#404040" : "#d9d9d9",
                  color: isDarkMode ? "#ffffff" : "#000000"
                }}>
                  <tr>
                    <th style={{ width: "42px" }}></th>
                    <th>Variant</th>
                    <th>Kitchen Name</th>
                    <th>Serving Size</th>
                    <th>{`Cost Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>Tax %</th>
                    <th>{`Sale Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>{`Delivery Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>{`Takeaway Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((variant, index) => (
                    <tr
                      key={index}
                      draggable
                      onClick={(e) => handleVariantRowClick(e, index)}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="align-middle text-center">
                        <i
                          className="fa fa-bars"
                          style={{ cursor: "grab", color: isDarkMode ? "#e2e8f0" : "#6c757d" }}
                          title="Drag to reorder"
                        ></i>
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="text"
                          value={variant.menuItem_Name || ""}
                          placeHolder="Enter Variant Name"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_Name",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="text"
                          value={variant.menuItem_KitchenDisplayName || ""}
                          placeHolder="Enter Kitchen Name"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_KitchenDisplayName",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="text"
                          value={variant.menuItem_ServingSize || ""}
                          placeHolder="1"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_ServingSize",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={
                            variant.menuItem_CostPrice !== undefined &&
                            variant.menuItem_CostPrice !== null &&
                            variant.menuItem_CostPrice !== ""
                              ? String(variant.menuItem_CostPrice)
                              : ""
                          }
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_CostPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={variant.menuItem_TaxPercentage ?? ""}
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_TaxPercentage",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={(variant.menuItem_SalePrice && variant.menuItem_SalePrice !== "0" && variant.menuItem_SalePrice !== 0) ? variant.menuItem_SalePrice.toString() : ""}
                          placeHolder="0.00"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          step="0.01"
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_SalePrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={(variant.menuItem_DeliveryPrice && variant.menuItem_DeliveryPrice !== "0" && variant.menuItem_DeliveryPrice !== 0) ? variant.menuItem_DeliveryPrice.toString() : ""}
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_DeliveryPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={(variant.menuItem_TakeAwayPrice && variant.menuItem_TakeAwayPrice !== "0" && variant.menuItem_TakeAwayPrice !== 0) ? variant.menuItem_TakeAwayPrice.toString() : ""}
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_TakeAwayPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center m-2 me-3">
                          <ToggleSwitch
                            isChecked={variant.menuItem_IsDefaultVariant}
                            label="Default"
                            draggable={false}
                            onDragStart={(e) => e.stopPropagation()}
                            onToggle={() =>
                              handleVariantChange(
                                index,
                                "menuItem_IsDefaultVariant",
                                !variant.menuItem_IsDefaultVariant
                              )
                            }
                            themeColor={themeColor}
                          />
                        </div>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center m-1">
                          <i
                            className="fa fa-eye text-primary me-3"
                            style={{ opacity: 0.8, cursor: "pointer" }}
                            title="Open variant details"
                            onClick={() => openVariantDetailModal(index)}
                          ></i>
                          <i
                            className="fa fa-trash text-danger m-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleRemoveVariant(index)}
                          ></i>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAdditionalOptions = () => (
    <div className="row g-4 mt-2">
      <div className="col-12">
        <div 
          className="card p-3"
          style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
            color: isDarkMode ? '#ffffff' : '#000000',
          }}
        >
          <h5 className="card-title mb-3">Additional Options</h5>
          <div className="row g-3 d-flex align-items-center p-4">
            <div className="col-2 d-flex">
              <ToggleSwitch
                isChecked={toggles.skuEnabled}
                onToggle={handleToggle("skuEnabled")}
                label="SKU Enable"
                name="skuEnabled"
                themeColor={themeColor}
              />
            </div>
            <div className="col-4 d-flex">
              {toggles.skuEnabled && (
                <FormInput
                  placeHolder="SKU Code"
                  type="text"
                  value={formState.menuItem_SKU}
                  onchange={handleInputChange("menuItem_SKU")}
                  className="form-control flex-grow-1"
                />
              )}
            </div>
            <div className="col-2 d-flex">
              <ToggleSwitch
                isChecked={toggles.expiryDateEnabled}
                onToggle={handleToggle("expiryDateEnabled")}
                label="Expiry Date"
                name="expiryDateEnabled"
                themeColor={themeColor}
              />
            </div>
            <div className="col-4 d-flex">
              {toggles.expiryDateEnabled && (
                <div style={{ width: '100%' }}>
                  {/* MUI-Style Date Picker */}
                  <DebugMUIDatePicker
              
                    value={expiryDate}
                    onChange={(date) => {
                      markUserEdited();
                      setExpiryDate(date);
                    }}
                    placeholder="Select expiry date"
                    name="expiryDate"
                    clearable={true}
                    minDate={new Date()} // Disable past dates
                  />
                  
                  {/* Or use the existing CustomDateTimePicker - uncomment below and comment above */}
                  {/* <CustomDateTimePicker
                    label="Expiry Date"
                    mode="date"
                    value={expiryDate}
                    onChange={(date) =>
                      setExpiryDate(date ? date.toDate() : null)
                    }
                    inputFormat="MM/DD/YYYY"
                    name="expiryDate"
                    isValidDate={disablePastDates}
                    placeholder="Select expiry date"
                    themeColor={themeColor}
                    components={{
                      OpenPickerIcon: () => (
                        <i
                          className="fa fa-calendar"
                          style={{ 
                            fontSize: "16px",
                            color: isDarkMode ? '#ffffff' : '#6c757d'
                          }}
                        />
                      ),
                    }}
                    inputProps={{
                      className: `form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`,
                      style: {
                        borderRadius: "10px",
                        border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                        background: isDarkMode ? '#2d3436' : '#F9F9F9',
                        color: isDarkMode ? '#ffffff' : '#000000',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      }
                    }}
                  /> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdditionalToggles = () => (
    <div className="row g-4">
      <div className="col-12">
        <div 
          className="card p-3"
          style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
            color: isDarkMode ? '#ffffff' : '#000000',
          }}
        >
          <h5 className="card-title mb-3">Additional Flags</h5>
          <div className="row row-cols-3 g-3">
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isPreventGainingLoyalty}
                onToggle={handleToggle("isPreventGainingLoyalty")}
                label="Prevent Loyalty Points"
                name="isPreventGainingLoyalty"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isShowNotificationExpDate}
                onToggle={handleToggle("isShowNotificationExpDate")}
                label="Show Expiry Notification"
                name="isShowNotificationExpDate"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isArchieve}
                onToggle={handleToggle("isArchieve")}
                label="Is Archived"
                name="isArchieve"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isAllowedtoSetPriceatThePointofSale}
                onToggle={handleToggle("isAllowedtoSetPriceatThePointofSale")}
                label="Set Price at POS"
                name="isAllowedtoSetPriceatThePointofSale"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isShowSocialMediaLink}
                onToggle={handleToggle("isShowSocialMediaLink")}
                label="Show Social Media Link"
                name="isShowSocialMediaLink"
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSEO = () => (
    <div className="col-12 mt-4">
      <div 
        className="card p-4"
        style={{
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
          color: isDarkMode ? '#ffffff' : '#000000',
        }}
      >
        <ToggleSwitch
          isChecked={toggles.seoEnabled}
          onToggle={handleToggle("seoEnabled")}
          label="Search Engine Optimization"
          name="seoEnabled"
          themeColor={themeColor}
        />
        {toggles.seoEnabled && (
          <div className="mt-3">
            <div className="row g-3">
              <div className="col-6">
                <FormInput
                  label="SEO Title"
                  type="text"
                  placeHolder="Enter SEO Title"
                  name="menuItem_SEOTitle"
                  value={formState.menuItem_SEOTitle}
                  onchange={handleInputChange("menuItem_SEOTitle")}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Permalink"
                  type="text"
                  placeHolder="Enter permalink"
                  name="menuItem_Permalink"
                  value={formState.menuItem_Permalink}
                  onchange={handleInputChange("menuItem_Permalink")}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Image Alt Text"
                  type="text"
                  placeHolder="Enter Image Alt Text"
                  name="menuItem_ImageAltText"
                  value={formState.menuItem_ImageAltText}
                  onchange={handleInputChange("menuItem_ImageAltText")}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-medium">SEO Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter Description of SEO"
                  value={formState.menuItem_SEODescription}
                  onChange={handleInputChange("menuItem_SEODescription")}
                  style={{
                    border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                    background: isDarkMode ? "#2d3436" : "#F9F9F9",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      
      {renderBreadcrumb()}
      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <div 
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              
              <div className="row mb-4 align-items-center">
                <div className="col-md-3 text-center">
                  <ImageUpload
                    imageUrl={imageState.menuItem_Image}
                    imageFile={imageState.imageFile}
                    setImage={(url, file, opts) => {
                      markUserEdited();
                      setImageState({ menuItem_Image: url, imageFile: file });
                      if (opts?.altText != null && String(opts.altText).trim() !== "") {
                        setFormState((prev) => ({
                          ...prev,
                          menuItem_ImageAltText: String(opts.altText).trim().slice(0, 500),
                        }));
                      }
                    }}
                    cropModalTitle="Crop menu item image"
                    mediaLibraryTitle="Menu image library"
                  />
                </div>
                <div className="col-md-9">{renderToggles()}</div>
              </div>
              {renderFormFields()}
              {!toggles.menuItem_HasVariant && renderAdditionalOptions()}
              <div className="col-12 mt-4">
                <div 
                  className="card p-3"
                  style={{
                    backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                    color: isDarkMode ? '#ffffff' : '#000000',
                  }}
                >
                  <h5 className="card-title mb-3">Selected Categories *</h5>
                  <CategorySelector
                    selectedCategories={selectors.selectedParentCategory}
                    setSelectedCategories={(updater) => {
                      markUserEdited();
                      setSelectors((prev) => {
                        const currentCats = prev.selectedParentCategory;
                        const newCats =
                          typeof updater === "function"
                            ? updater(currentCats)
                            : updater;
                        return { ...prev, selectedParentCategory: newCats };
                      });
                    }}
                    showModal={modals.showParentCategoryModal}
                    setShowModal={(val) => val ? openModal("showParentCategoryModal") : closeAllModals()}
                    onModalSubmit={handleSelectorSubmit(
                      "selectedParentCategory"
                    )}
                    setLoading={setLoading}
                  />

                  {/* Reporting Category Display - Only show when multiple categories are selected */}
                  {selectors.selectedParentCategory.length > 1 && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-center">
                        <div
                          className="reporting-category-disclaimer"
                          style={{
                            background: "#f8f9fa",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            border: "1px solid #e9ecef",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            fontSize: "14px",
                            color: "#6c757d",
                            maxWidth: "fit-content",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#e9ecef";
                            e.currentTarget.style.borderColor = "#007bff";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#f8f9fa";
                            e.currentTarget.style.borderColor = "#e9ecef";
                          }}
                        >
                          {/* Small Info Icon */}
                          <i
                            className="fa fa-info-circle"
                            style={{ fontSize: "14px", color: "#007bff" }}
                          ></i>

                          {/* Disclaimer Text */}
                          <span>
                            Reporting Category:{" "}
                            <strong style={{ color: "#495057" }}>
                              {reportingCategory?.rootCat_Name ||
                                reportingCategory?.rC_Name ||
                                "Not selected"}
                            </strong>
                          </span>

                          {/* Small Change Icon */}
                          <button
                            onClick={() => openModal("showReportingCategoryModal")}
                            style={{
                              background: "transparent",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px",
                              color: "#007bff",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "24px",
                              height: "24px",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#007bff";
                              e.currentTarget.style.color = "white";
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "transparent";
                              e.currentTarget.style.color = "#007bff";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            <i
                              className="fa fa-edit"
                              style={{ fontSize: "10px" }}
                            ></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {formState.menuItem_Type !== "Combo" && (
                <div className="col-12 mt-4">
                  <ModifierGroupsSelector
                    isEnabled={toggles.modifierGroupsEnabled}
                    setIsEnabled={handleToggle("modifierGroupsEnabled")}
                    selectedGroups={selectors.selectedModifierGroups}
                    setSelectedGroups={(groups) => {
                      markUserEdited();
                      setSelectors((prev) => ({
                        ...prev,
                        selectedModifierGroups: dedupeModifierGroups(Array.isArray(groups) ? groups : []),
                      }));
                    }}
                    showModal={modals.showModifierModal}
                    setShowModal={(val) => val ? openModal("showModifierModal") : closeAllModals()}
                    onModalSubmit={handleSelectorSubmit(
                      "selectedModifierGroups"
                    )}
                    setLoading={setLoading}
                  />
                </div>
              )}
              <div className="col-12 mt-4">
                <SupplierSelector
                  isEnabled={toggles.suppliersEnabled}
                  setIsEnabled={handleToggle("suppliersEnabled")}
                  selectedSuppliers={selectors.selectedSuppliers}
                  setSelectedSuppliers={(suppliers) => {
                    markUserEdited();
                    setSelectors((prev) => ({
                      ...prev,
                      selectedSuppliers: Array.isArray(suppliers) ? suppliers : [],
                    }));
                  }}
                  showModal={modals.showSupplierModal}
                  setShowModal={(val) => val ? openModal("showSupplierModal") : closeAllModals()}
                  onModalSubmit={handleSelectorSubmit("selectedSuppliers")}
                  setLoading={setLoading}
                />
              </div>
              <div className="col-12 mt-4">
                <AllergenSelector
                  isEnabled={toggles.allergensEnabled}
                  setIsEnabled={handleToggle("allergensEnabled")}
                  selectedAllergens={selectors.selectedAllergens}
                  setSelectedAllergens={(allergens) => {
                    markUserEdited();
                    setSelectors((prev) => ({
                      ...prev,
                      selectedAllergens: resolveSelectedAllergens(
                        allergens,
                        allAllergens
                      ),
                    }));
                  }}
                  showModal={modals.showAllergenModal}
                  setShowModal={(val) => val ? openModal("showAllergenModal") : closeAllModals()}
                  onModalSubmit={handleSelectorSubmit("selectedAllergens")}
                  allergens={allAllergens}
                  setLoading={setLoading} // This is the problematic prop
                />
              </div>
              {IsCombo && (
                <div className="col-12 mt-4">
                  <h5 className="card-title mb-3">Selected Products</h5>
                  <div 
                    className="card p-4"
                    style={{
                      backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                      border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                      color: isDarkMode ? '#ffffff' : '#000000',
                    }}
                  >
                    <div className="mb-3">
                      <ToggleSwitch
                        isChecked={toggles.productsEnabled}
                        onToggle={handleToggle("productsEnabled")}
                        label="Select Products"
                        name="productsEnabled"
                        themeColor={themeColor}
                      />
                    </div>
                    {toggles.productsEnabled && (
                      <>
                        {selectors.selectedProducts.length > 0 ? (
                          <div className="mt-3">
                            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                              {selectors.selectedProducts.map((product) => (
                                <div key={product.menuItem_ID} className="col">
                                  <div
                                    className="product-chip"
                                    style={{
                                      background: "#ffffff",
                                      borderRadius: "12px",
                                      padding: "16px 20px",
                                      border: "2px solid #e9ecef",
                                      transition: "all 0.2s ease",
                                      cursor: "default",
                                      position: "relative",
                                      boxShadow:
                                        "0 2px 8px rgba(0, 0, 0, 0.05)",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor =
                                        "#007bff";
                                      e.currentTarget.style.boxShadow =
                                        "0 4px 12px rgba(0, 123, 255, 0.15)";
                                      e.currentTarget.style.transform =
                                        "translateY(-2px)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor =
                                        "#e9ecef";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(0, 0, 0, 0.05)";
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="d-flex align-items-center">
                                        {/* Product Image/Icon */}
                                        <div
                                          style={{
                                            width: "36px",
                                            height: "36px",
                                            background: "#f8f9fa",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginRight: "12px",
                                            border: "1px solid #e9ecef",
                                            overflow: "hidden",
                                          }}
                                        >
                                          {product.menuItem_Image ? (
                                            <img
                                              src={product.menuItem_Image}
                                              alt={product.menuItem_Name}
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                borderRadius: "6px",
                                              }}
                                            />
                                          ) : (
                                            <i
                                              className="fa fa-box"
                                              style={{
                                                fontSize: "14px",
                                                color: "#6c757d",
                                              }}
                                            ></i>
                                          )}
                                        </div>

                                        {/* Product Name */}
                                        <div>
                                          <span
                                            style={{
                                              fontSize: "15px",
                                              fontWeight: "500",
                                              color: "#495057",
                                            }}
                                          >
                                            {product.menuItem_Name}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="d-flex align-items-center gap-2">
                                        {/* Quantity Input */}
                                        <FormInput
                                          type="text"
                                          value={product.availableQuantity}
                                          onchange={(e) =>
                                            handleProductQuantityChange(
                                              product.menuItem_ID,
                                              e.target.value
                                            )
                                          }
                                          placeHolder="Qty"
                                          style={{
                                            width: "60px",
                                            fontSize: "12px",
                                            padding: "4px 8px",
                                            border: "1px solid #e9ecef",
                                            borderRadius: "4px",
                                            textAlign: "center",
                                          }}
                                        />

                                        {/* Remove Button */}
                                        <button
                                          onClick={() =>
                                            removeProduct(product.menuItem_ID)
                                          }
                                          style={{
                                            background: "transparent",
                                            border: "none",
                                            borderRadius: "6px",
                                            width: "28px",
                                            height: "28px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            color: "#dc3545",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.background =
                                              "#f8d7da";
                                            e.currentTarget.style.color =
                                              "#721c24";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.background =
                                              "transparent";
                                            e.currentTarget.style.color =
                                              "#dc3545";
                                          }}
                                        >
                                          <i
                                            className="fa fa-times"
                                            style={{
                                              fontSize: "12px",
                                            }}
                                          ></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="d-flex justify-content-center mt-4">
                              <button
                                onClick={() => openModal("showProductModal")}
                                style={{
                                  background: "#007bff",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "10px 20px",
                                  color: "white",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#0056b3";
                                  e.currentTarget.style.transform =
                                    "translateY(-1px)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#007bff";
                                  e.currentTarget.style.transform =
                                    "translateY(0)";
                                }}
                              >
                                <i
                                  className="fa-solid fa-plus"
                                  style={{ fontSize: "12px" }}
                                ></i>
                                Add More Products
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <div
                              style={{
                                width: "60px",
                                height: "60px",
                                background: "#f8f9fa",
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 16px",
                                border: "1px solid #e9ecef",
                              }}
                            >
                              <i
                                className="fa fa-box"
                                style={{ fontSize: "24px", color: "#6c757d" }}
                              ></i>
                            </div>
                            <p
                              style={{
                                color: "#6c757d",
                                fontSize: "15px",
                                marginBottom: "20px",
                              }}
                            >
                              No products selected
                            </p>
                            <button
                              onClick={() => openModal("showProductModal")}
                              style={{
                                background: "#007bff",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                color: "white",
                                fontSize: "14px",
                                fontWeight: "500",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                margin: "0 auto",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#0056b3";
                                e.currentTarget.style.transform =
                                  "translateY(-1px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#007bff";
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                              }}
                            >
                              <i
                                className="fa-solid fa-plus"
                                style={{ fontSize: "12px" }}
                              ></i>
                              Add Products
                            </button>
                          </div>
                        )}
                        <ProductMultiSelectionModal
                          isEnabled={toggles.productsEnabled}
                          setIsEnabled={handleToggle("productsEnabled")}
                          selectedProducts={selectors.selectedProducts}
                          setSelectedProducts={(products) => {
                            markUserEdited();
                            setSelectors((prev) => ({
                              ...prev,
                              selectedProducts: products.map((p) => ({
                                ...p,
                                availableQuantity: p.availableQuantity || "1",
                              })),
                            }));
                          }}
                          showModal={modals.showProductModal}
                          setShowModal={(val) => val ? openModal("showProductModal") : closeAllModals()}
                          onModalSubmit={handleSelectorSubmit(
                            "selectedProducts"
                          )}
                          setLoading={setLoading}
                          showButton={true}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="col-12 mt-4">
                <MenuGroupsSelector
                  isEnabled={toggles.menuGroupsEnabled}
                  setIsEnabled={handleToggle("menuGroupsEnabled")}
                  selectedGroups={selectors.selectedMenuGroups}
                  setSelectedGroups={(groups) => {
                    markUserEdited();
                    setSelectors((prev) => ({
                      ...prev,
                      selectedMenuGroups: Array.isArray(groups) ? groups : [],
                    }));
                  }}
                  showModal={modals.showMenuGroupModal}
                  setShowModal={(val) => val ? openModal("showMenuGroupModal") : closeAllModals()}
                  onModalSubmit={handleSelectorSubmit("selectedMenuGroups")}
                  setLoading={setLoading}
                />
              </div>

              {renderSEO()}

              <div className="row mt-4" style={{ display: 'none' }}>
                <div className="col-6">
                  <div 
                    className="card p-3 mb-4"
                    style={{
                      backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                      border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                      color: isDarkMode ? '#ffffff' : '#000000',
                    }}
                  >
                    <h5 className="card-title mb-3">Other</h5>
                    <div className="row g-3">
                      <div className="col-6 mb-3">
                        <label 
                          className="form-label fw-medium"
                          style={{
                            color: isDarkMode ? '#ffffff' : '#000000'
                          }}
                        >
                          Available Start Time
                        </label>
                        <DateTime
                          dateFormat={false}
                          timeFormat="HH:mm"
                          value={formState.menuItem_ASTime}
                          onChange={handleASTimeChange}
                          inputProps={{
                            placeholder: "Select start time (e.g., 09:00)",
                            className: `form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`,
                            style: {
                              borderRadius: "10px",
                              border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                              background: isDarkMode ? '#2d3436' : '#F9F9F9',
                              color: isDarkMode ? '#ffffff' : '#000000',
                              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            }
                          }}
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label 
                          className="form-label fw-medium"
                          style={{
                            color: isDarkMode ? '#ffffff' : '#000000'
                          }}
                        >
                          Available End Time
                        </label>
                        <DateTime
                          dateFormat={false}
                          timeFormat="HH:mm"
                          value={formState.menuItem_AETime}
                          onChange={handleAETimeChange}
                          inputProps={{
                            placeholder: "Select end time (e.g., 17:00)",
                            className: `form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`,
                            style: {
                              borderRadius: "10px",
                              border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                              background: isDarkMode ? '#2d3436' : '#F9F9F9',
                              color: isDarkMode ? '#ffffff' : '#000000',
                              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <FormInput
                          label="Rating"
                          value={formState.menuItem_Rating}
                          onchange={handleInputChange("menuItem_Rating")}
                          type="number"
                          placeHolder="Rating (0-5)"
                          name="menuItem_Rating"
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <FormInput
                          label="Barcode"
                          value={formState.menuItem_BarCode}
                          onchange={handleInputChange("menuItem_BarCode")}
                          type="text"
                          placeHolder="Barcode"
                          name="menuItem_BarCode"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6">{renderAdditionalToggles()}</div>
              </div>
              <CustomModal
                title="What type of item do you want to create"
                fontSize="18px"
                fontWeight={400}
                onClose={closeAllModals}
                isOpen={modals.isModalOpen}
                isCentered={true}
              >
                <ChoiceButtonGrid
                  activeButton={formState.menuItem_RootType}
                  handleClick={handleProductTypeSelect}
                />
              </CustomModal>
              {renderVariantDetailModal()}
              <ReportingCategoryModel
                isModalOpen={modals.showReportingCategoryModal}
                setIsModalOpen={(val) => val ? openModal("showReportingCategoryModal") : closeAllModals()}
                onCategorySubmit={handleReportingCategorySubmit}
                compID={CompID}
                selectedCategory={reportingCategory}
                categories={selectors.selectedParentCategory}
                showButton={true}
                onRefresh={refreshReportingCategoriesFromTree}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
};

export default CreateItem;
